import { useCallback, useEffect, useRef, useState } from "react";
import type { IDAChatMessage } from "../utils/chatMessageMapper";
import type { GptLiveTranscriptTurn } from "../services/gptLiveVoiceTransport";
import { reconcileProvisionalVoiceMessages } from "../utils/voiceTranscriptMessages";

export function useGptLiveTranscriptMessages(
  persistedMessages: IDAChatMessage[],
  resetKey: string | number | null,
  currentUserId?: string | null,
) {
  const [messages, setMessages] = useState<IDAChatMessage[]>([]);
  const messagesRef = useRef<IDAChatMessage[]>([]);
  const persistedMessagesRef = useRef(persistedMessages);
  const latestBatchSequenceRef = useRef<number | null>(null);
  const activeVoiceSessionRef = useRef<string | null>(null);

  useEffect(() => {
    persistedMessagesRef.current = persistedMessages;
  }, [persistedMessages]);

  useEffect(() => {
    messagesRef.current = [];
    latestBatchSequenceRef.current = null;
    activeVoiceSessionRef.current = null;
    setMessages([]);
  }, [resetKey]);

  useEffect(() => {
    const remaining = reconcileProvisionalVoiceMessages(
      messages,
      persistedMessages,
    );
    if (remaining.length === messages.length) return;
    messagesRef.current = remaining;
    setMessages(remaining);
  }, [messages, persistedMessages]);

  const updateTranscriptTurns = useCallback(
    (turns: GptLiveTranscriptTurn[]) => {
      if (turns.length === 0) return;
      const batchId = turns[0].batchId;
      const batchSequenceMatch = batchId.match(/:batch:(\d+)$/);
      const batchSessionId = batchSequenceMatch
        ? batchId.slice(0, batchSequenceMatch.index)
        : null;
      const batchSequence = batchSequenceMatch
        ? Number(batchSequenceMatch[1])
        : null;
      if (
        batchSessionId !== null &&
        activeVoiceSessionRef.current !== batchSessionId
      ) {
        activeVoiceSessionRef.current = batchSessionId;
        latestBatchSequenceRef.current = null;
      }
      const mayBeLatestBatch =
        batchSequence === null ||
        latestBatchSequenceRef.current === null ||
        batchSequence >= latestBatchSequenceRef.current;
      if (batchSequence !== null && mayBeLatestBatch) {
        latestBatchSequenceRef.current = batchSequence;
      }
      const existingBatchMessages = messagesRef.current.filter(
        (message) => message.metadata?.gpt_live_batch_id === batchId,
      );
      const existingByTurnId = new Map(
        existingBatchMessages.map((message) => [
          message.metadata?.gpt_live_turn_id,
          message,
        ]),
      );
      const nextBatchMessages = turns.map((turn): IDAChatMessage => {
        const existing = existingByTurnId.get(turn.turnId);
        return {
          id: turn.turnId,
          role: turn.speaker,
          content: turn.content,
          createdAt: existing?.createdAt ?? new Date(),
          isStreaming:
            mayBeLatestBatch && turn.speaker === "assistant" && !turn.isFinal,
          metadata: {
            ...existing?.metadata,
            source: "voice",
            speaker: turn.speaker,
            gpt_live_batch_id: batchId,
            gpt_live_turn_id: turn.turnId,
            gpt_live_run_complete: turn.isComplete,
            ...(turn.providerSessionId
              ? { provider_session_id: turn.providerSessionId }
              : {}),
            ...(turn.providerEventIds.length
              ? { provider_event_ids: turn.providerEventIds }
              : {}),
            ...(turn.startMs !== null ? { start_ms: turn.startMs } : {}),
            ...(turn.endMs !== null ? { end_ms: turn.endMs } : {}),
            ...(typeof turn.delegatedAnswerStartedAt === "number"
              ? {
                  gpt_live_delegation_started_at: turn.delegatedAnswerStartedAt,
                }
              : {}),
            ...(turn.speaker === "user" && currentUserId
              ? { sender_user_id: currentUserId }
              : {}),
          },
        };
      });
      const keptMessages = messagesRef.current
        .filter((message) => message.metadata?.gpt_live_batch_id !== batchId)
        .map((message) =>
          mayBeLatestBatch &&
          message.role === "assistant" &&
          message.isStreaming
            ? { ...message, isStreaming: false }
            : message,
        );
      const unfiltered = [...keptMessages, ...nextBatchMessages];
      const nextMessages = reconcileProvisionalVoiceMessages(
        unfiltered,
        persistedMessagesRef.current,
      );
      messagesRef.current = nextMessages;
      setMessages(nextMessages);
    },
    [currentUserId],
  );

  return {
    messages,
    updateTranscriptTurns,
  };
}
